import { numberFormat } from '@enity/formatting'
import i18n from '@libs/internationalization'
import { mergeSeries } from '@libs/models/charts/charts'
import { chartFontSizes, formatCurrency } from '@libs/models/formatters'
import type Highcharts from 'highcharts'
import { ComparisonEnum, type ConsumptionType } from '../../interfaces/interfaces'

export type ColumnChartData = {
	config?: Highcharts.Options;
	title?: string;
	subTitle?: string;
	categories: string[];
	data: [
		{
			name: string;
			y: number;
		},
	][];
	rotateYLabels: boolean;
	comparison: ComparisonEnum;
	showYAxis: boolean;
	legends: string[];
	unit: string;
	consumptionType: ConsumptionType;
};

export default ({
	config,
	title,
	subTitle,
	categories,
	data,
	rotateYLabels,
	comparison,
	showYAxis,
	legends,
	unit,
	consumptionType,
}: ColumnChartData): Highcharts.Options => {
	const fontSizes = chartFontSizes()
	const defaultConfig = {
		chart: {
			backgroundColor: 'transparent',
			events: {
				// eslint-disable-next-line
				load: function (this, event: any) {
					//  event is of type Event, but it's painfully generic
					const min = event.target?.yAxis[0].min
					const max = event.target?.yAxis[0].max
					const dataMax = event.target?.yAxis[0].chart.yAxis[0].dataMax
					if (dataMax > max) {
						// eslint-disable-next-line
						event.target?.yAxis[0].setExtremes(null, dataMax);
					} else if (dataMax < min) {
						// eslint-disable-next-line
						event.target?.yAxis[0].setExtremes(dataMax, null);
					}
				},
			},
			height: '600px',
			marginTop: 200,
			reflow: true,
			spacingLeft: 0,
			spacingRight: 0,
			type: 'column',
		},
		credits: {
			enabled: false,
		},
		legend: {
			enabled: comparison === ComparisonEnum.periodically,
			floating: true,
			itemStyle: {
				color: '#ffffff',
				fontSize: fontSizes.legend,
				fontWeight: 'regular',
			},
			label: {
				style: {
					color: '#fff',
				},
			},
			labelFormatter: function() {
				const hasStacked = data.length > 2
				const index = hasStacked ? this.index - 1 : this.index
				return legends[index]
			},
			symbolRadius: 2,
			y: 50,
		},
		plotOptions: {
			column: {
				colorByPoint: (data[0]?.length ?? 0) <= 2,
				colors:
					(data[0]?.length ?? 0) <= 2
						? [config?.series?.[1]?.color, config?.series?.[0]?.color].filter((c): c is string => c !== undefined)
						: undefined, // accumulated compare column is lighter color, taken from comparisonSeries
				groupPadding: (data[0]?.length ?? 0) <= 2 ? 0 : 0.1,
			},
			showInLegend: false,
		},
		series: [
			{
				// default object, will most likely be overwritten!
				borderWidth: 0,
				color: '#9bd4b5',
				data: [] as number[],
				dataLabels: {
					align: rotateYLabels ? 'left' : undefined,
					animation: true,
					enabled: !showYAxis, // Hide data labels when the y axis is visible and vice versa
					rotation: rotateYLabels ? -90 : 0,
					style: {
						color: '#fff',
						fontFamily: 'Trebuchet MS',
						fontSize: fontSizes.dataLabel,
						textOutline: 'transparent',
					},
				},
				type: 'column',
			},
		],
		subtitle: {
			style: {
				color: '#fff',
				fontSize: fontSizes.subtitle,
			},
			text: subTitle,
		},
		title: {
			style: {
				color: '#fff',
				fontSize: fontSizes.title,
			},
			text: title,
		},
		tooltip: {
			enabled: false,
		},
		xAxis: {
			categories,
			labels: {
				style: {
					color: '#fff',
					fontSize: fontSizes.axisLabel,
					textOverflow: 'none',
				},
			},
			lineWidth: 0,
		},
		yAxis: {
			labels: {
				formatter: function() {
					return consumptionType === 'cost'
						? formatCurrency(unit, this.value as number)
						: numberFormat(i18n.global.locale, this.value as number) + ' ' + unit
				},
				style: {
					color: '#ffffff',
					fontSize: fontSizes.yAxisLabel,
				},
			},
			title: {
				text: undefined,
			},
			visible: showYAxis,
		},
	} as Highcharts.Options

	if (config) mergeSeries(config, defaultConfig as Highcharts.Options, data)
	return defaultConfig as Highcharts.Options
}
